import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight, Download, Lock, Unlock } from "lucide-react";

const downloadCsv = async (url, filename) => {
  const BASE = process.env.REACT_APP_API_URL || "";
  const res = await fetch(`${BASE}${url}`, { credentials: "include" });
  if (!res.ok) throw new Error("Export failed");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

const addWeeks = (iso, n) => {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n * 7);
  return d.toISOString().slice(0, 10);
};

const thisMonday = () => {
  const d = new Date();
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d.toISOString().slice(0, 10);
};

const fmtDate = (iso) =>
  new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });

const STATUS_ICON = {
  approved: <CheckCircle className="w-3.5 h-3.5 text-[#00F5D4]" />,
  rejected: <XCircle className="w-3.5 h-3.5 text-[#FF007A]" />,
  pending: <Clock className="w-3.5 h-3.5 text-[#F59E0B]" />,
};

const StatusBadge = ({ status }) => {
  const colours = {
    approved: "text-[#00F5D4] bg-[#00F5D4]/10",
    rejected: "text-[#FF007A] bg-[#FF007A]/10",
    pending: "text-[#F59E0B] bg-[#F59E0B]/10",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] ${colours[status] || colours.pending}`}>
      {STATUS_ICON[status]} {status || "pending"}
    </span>
  );
};

const Timesheets = () => {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "owner";

  const [weekStart, setWeekStart] = useState(thisMonday());
  const [viewStatus, setViewStatus] = useState("pending"); // "pending" | "approved" — approved tab is for reopening locked entries
  const [pending, setPending] = useState([]);
  const [summary, setSummary] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [noteMap, setNoteMap] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isManager) {
        const [p, s] = await Promise.all([
          api.get(`/timesheets/pending?week_start=${weekStart}&status=${viewStatus}`),
          api.get(`/timesheets/summary?week_start=${weekStart}`),
        ]);
        setPending(p.data);
        setSummary(s.data);
      } else {
        const s = await api.get(`/timesheets/summary?week_start=${weekStart}&user_id=${user?.user_id}`);
        setSummary(s.data);
      }
    } catch { toast.error("Failed to load timesheets"); }
    finally { setLoading(false); }
  }, [weekStart, viewStatus, isManager, user?.user_id]);

  useEffect(() => { load(); }, [load]);

  const toggleAll = () => {
    if (selected.size === pending.length) setSelected(new Set());
    else setSelected(new Set(pending.map((l) => l.log_id)));
  };

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const bulkAction = async (action) => {
    if (!selected.size) return toast.error("Select at least one entry");
    setBusy(true);
    try {
      await api.post("/timesheets/approve", {
        log_ids: [...selected],
        action,
        note: noteMap[action] || "",
      });
      toast.success(action === "approve" ? "Approved" : action === "reject" ? "Rejected" : "Reopened for editing");
      setSelected(new Set());
      setNoteMap({});
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const grouped = pending.reduce((acc, log) => {
    const key = log.user_id || "unknown";
    if (!acc[key]) acc[key] = { name: log.user_name || log.user_id, logs: [] };
    acc[key].logs.push(log);
    return acc;
  }, {});

  return (
    <div>
      <PageHeader
        eyebrow="People"
        title="Timesheets"
        description="Review and approve time logged by your team."
        actions={
          <Button
            variant="outline"
            onClick={() => downloadCsv(`/api/time-logs/export?week_start=${weekStart}`, `time-logs-${weekStart}.csv`).catch(() => toast.error("Export failed"))}
            className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900"
            data-testid="export-timesheets-btn"
          >
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 py-6 space-y-6">
        {/* Week nav */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => setWeekStart((w) => addWeeks(w, -1))} className="rounded-none h-8 w-8 p-0 text-[#6B7280] hover:text-white border border-[#1F2937]">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-[#9CA3AF] font-mono">Week of {fmtDate(weekStart)}</span>
          <Button variant="ghost" onClick={() => setWeekStart((w) => addWeeks(w, 1))} className="rounded-none h-8 w-8 p-0 text-[#6B7280] hover:text-white border border-[#1F2937]">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Summary cards */}
        {summary.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {summary.map((row) => (
              <div key={row.user_id} className="border border-[#1F2937] bg-[#0D1117] p-4">
                <div className="text-xs font-medium text-white truncate">{row.name || row.user_id}</div>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-[11px]">
                    <span className="text-[#6B7280]">Total</span>
                    <span className="text-white font-mono">{row.total_hours}h</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-[#00F5D4]">Approved</span>
                    <span className="text-[#00F5D4] font-mono">{row.approved_hours}h</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-[#F59E0B]">Pending</span>
                    <span className="text-[#F59E0B] font-mono">{row.pending_hours}h</span>
                  </div>
                  {row.rejected_hours > 0 && (
                    <div className="flex justify-between text-[11px]">
                      <span className="text-[#FF007A]">Rejected</span>
                      <span className="text-[#FF007A] font-mono">{row.rejected_hours}h</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Manager approval table */}
        {isManager && (
          <div className="border border-[#1F2937]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#1F2937] bg-[#111827] flex-wrap gap-3">
              <div className="flex items-center gap-4">
                <div className="flex border border-[#374151]">
                  {[
                    { key: "pending", label: "Pending" },
                    { key: "approved", label: "Approved · locked" },
                  ].map((t) => (
                    <button
                      key={t.key}
                      onClick={() => { setViewStatus(t.key); setSelected(new Set()); }}
                      className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] ${viewStatus === t.key ? "bg-[#00F5D4]/10 text-[#00F5D4]" : "text-[#6B7280] hover:text-white"}`}
                      data-testid={`timesheets-tab-${t.key}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280]">
                  {pending.length} entries
                </div>
              </div>
              {selected.size > 0 && viewStatus === "pending" && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#9CA3AF]">{selected.size} selected</span>
                  <input
                    value={noteMap.reject || ""}
                    onChange={(e) => setNoteMap((prev) => ({ ...prev, reject: e.target.value }))}
                    placeholder="Rejection note…"
                    className="h-7 px-2 text-xs rounded-none border border-[#374151] bg-[#030712] text-white w-44 placeholder:text-[#4B5563]"
                  />
                  <Button
                    size="sm"
                    onClick={() => bulkAction("approve")}
                    disabled={busy}
                    className="rounded-none h-7 bg-[#00F5D4]/10 text-[#00F5D4] hover:bg-[#00F5D4]/20 border border-[#00F5D4]/30 text-xs"
                    data-testid="bulk-approve-btn"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => bulkAction("reject")}
                    disabled={busy}
                    className="rounded-none h-7 bg-[#FF007A]/10 text-[#FF007A] hover:bg-[#FF007A]/20 border border-[#FF007A]/30 text-xs"
                    data-testid="bulk-reject-btn"
                  >
                    <XCircle className="w-3 h-3 mr-1" /> Reject
                  </Button>
                </div>
              )}
              {selected.size > 0 && viewStatus === "approved" && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#9CA3AF]">{selected.size} selected</span>
                  <input
                    value={noteMap.reopen || ""}
                    onChange={(e) => setNoteMap((prev) => ({ ...prev, reopen: e.target.value }))}
                    placeholder="Reopen note (optional)…"
                    className="h-7 px-2 text-xs rounded-none border border-[#374151] bg-[#030712] text-white w-44 placeholder:text-[#4B5563]"
                  />
                  <Button
                    size="sm"
                    onClick={() => bulkAction("reopen")}
                    disabled={busy}
                    className="rounded-none h-7 bg-[#F59E0B]/10 text-[#F59E0B] hover:bg-[#F59E0B]/20 border border-[#F59E0B]/30 text-xs"
                    data-testid="bulk-reopen-btn"
                  >
                    <Unlock className="w-3 h-3 mr-1" /> Reopen for editing
                  </Button>
                </div>
              )}
            </div>

            {loading ? (
              <div className="py-12 text-center text-sm text-[#6B7280]">Loading…</div>
            ) : pending.length === 0 ? (
              <div className="py-12 text-center text-sm text-[#6B7280]" data-testid="timesheets-empty">
                {viewStatus === "approved" ? "No approved entries for this week." : "No pending entries for this week."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[480px]">
                  <thead>
                    <tr className="border-b border-[#1F2937]">
                      <th className="w-10 px-4 py-3 bg-[#0D1117]">
                        <input
                          type="checkbox"
                          checked={selected.size === pending.length && pending.length > 0}
                          onChange={toggleAll}
                          className="accent-[#00F5D4]"
                          data-testid="select-all-timesheets"
                        />
                      </th>
                      <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Person</th>
                      <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Date</th>
                      <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Project</th>
                      <th className="hidden sm:table-cell text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Description</th>
                      <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Hours</th>
                      <th className="text-center px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(grouped).map((group) => (
                      group.logs.map((log, i) => (
                        <tr key={log.log_id} className="border-b border-[#1F2937] bg-[#0D1117] hover:bg-[#030712]" data-testid={`timesheet-row-${log.log_id}`}>
                          <td className="px-4 py-2.5">
                            <input
                              type="checkbox"
                              checked={selected.has(log.log_id)}
                              onChange={() => toggle(log.log_id)}
                              className="accent-[#00F5D4]"
                            />
                          </td>
                          <td className="px-4 py-2.5 text-white">
                            {i === 0 ? group.name : <span className="text-[#374151]">↳</span>}
                          </td>
                          <td className="px-4 py-2.5 text-[#9CA3AF] font-mono text-xs">
                            {log.date_logged ? fmtDate(log.date_logged) : "—"}
                          </td>
                          <td className="px-4 py-2.5 text-[#9CA3AF] truncate max-w-[120px]">
                            {log.project_name || log.project_id || "—"}
                          </td>
                          <td className="hidden sm:table-cell px-4 py-2.5 text-[#6B7280] truncate max-w-[200px]">
                            {log.description || "—"}
                          </td>
                          <td className="px-4 py-2.5 text-right text-white font-mono">{log.hours_spent ?? log.hours}h</td>
                          <td className="px-4 py-2.5 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <StatusBadge status={log.approval_status} />
                              {log.approval_status === "approved" && (
                                <Lock className="w-3 h-3 text-[#4B5563]" aria-label="Locked" />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Timesheets;
